// Control center (CR #181) — the taskbar clock is now a trigger that drops
// down one combined panel: mini month calendar, notification center, Erik's
// to-do list (same localStorage store as /my-tasks), the open dev-task queue,
// and the shortcut icons that used to sit loose on the bar (My Tasks, Dev
// Dashboard, Settings). Everything it opens becomes a floating window (#182).
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/hooks/useAuth';
import { useWindowManager } from '@/lib/context/WindowManagerContext';
import { useDesktopModeSetting } from '@/lib/hooks/useDesktopMode';
import apiClient from '@lib/api/client';
import {
  getNotifications, getUnreadCount, markRead, markAllRead, deleteNotification,
  type AppNotification,
} from '@/lib/api/notifications';
import { getDashboardStats, type DashboardStats } from '@/lib/api/dashboard';
import { bcStatHolidays } from '@/lib/utils/bcHolidays';
import { getAgenda, AGENDA_META, type AgendaItem } from '@/lib/api/agenda';

// ── To-dos — same store the /my-tasks page persists to ────────────────────────

interface AdminTask {
  id: string;
  text: string;
  done: boolean;
  category: 'routine' | 'todo';
  priority: 'high' | 'medium' | 'low';
  createdAt: string;
  dueDate?: string;
}

const TASKS_KEY = 'ochrepoint_admin_tasks';

const readTasks = (): AdminTask[] => {
  try { return JSON.parse(localStorage.getItem(TASKS_KEY) || '[]'); } catch { return []; }
};
const writeTasks = (tasks: AdminTask[]) => {
  localStorage.setItem(TASKS_KEY, JSON.stringify(tasks));
};

// ── Dev queue rows (owner only) ───────────────────────────────────────────────

interface DevQueueRow {
  id: number;
  title: string;
  page: string | null;
  status: string;
  priority: string | null;
  fulfill_now: 0 | 1;
}

const notifIcon: Record<string, string> = {
  invoice_overdue: 'receipt_long',
  invoice_paid: 'paid',
  invoice_sent: 'send',
  job_in_progress: 'construction',
  job_started: 'construction',
  job_completed: 'task_alt',
  job_on_hold: 'pause_circle',
  job_cancelled: 'cancel',
  quote_approved: 'thumb_up',
  quote_rejected: 'thumb_down',
  change_order: 'edit_note',
  time_submitted: 'schedule',
  task_overdue: 'assignment_late',
  work_lead: 'construction',
  warning: 'warning',
  default: 'notifications',
};

const pad = (n: number) => String(n).padStart(2, '0');
const calRoute = (d: Date) => `/calendar/day/${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${d.getFullYear()}`;

function SectionTitle({ icon, label, right }: { icon: string; label: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 px-3 pt-3 pb-1.5">
      <span className="material-symbols-outlined text-base text-[#C77D3F]">{icon}</span>
      <span className="flex-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">{label}</span>
      {right}
    </div>
  );
}

export function ControlCenter() {
  const { user, logout } = useAuth();
  const { openWindow } = useWindowManager();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Desktop Mode trial kill switch (#133) — moved here from the old settings
  // gear when the bar icons collapsed into this panel. Mirrored in Settings.
  const [desktopModeOn, setDesktopModeOn] = useDesktopModeSetting();

  // Clock
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  // Everything the panel opens becomes a window (#182), and the panel closes.
  const launch = (route: string, title: string, icon: string) => {
    setOpen(false);
    openWindow({ route, title, icon });
  };

  // ── Notifications (badge polls even while closed, like the old bell) ────────
  const [notes, setNotes] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const loadNotes = useCallback(async () => {
    try {
      const [n, count] = await Promise.all([getNotifications(), getUnreadCount()]);
      setNotes(n);
      setUnread(count);
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    loadNotes();
    const id = setInterval(loadNotes, 60_000);
    return () => clearInterval(id);
  }, [loadNotes]);

  const clickNote = async (n: AppNotification) => {
    if (!n.is_read) await markRead(n.id).catch(() => {});
    setNotes(prev => prev.map(x => (x.id === n.id ? { ...x, is_read: true } : x)));
    setUnread(prev => Math.max(0, prev - (n.is_read ? 0 : 1)));
    if (n.link) launch(n.link, n.title, notifIcon[n.type] ?? notifIcon.default);
  };

  // ── Mini calendar ───────────────────────────────────────────────────────────
  const today = now;
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const [scheduledDays, setScheduledDays] = useState<Set<string>>(new Set());
  const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  useEffect(() => {
    if (!open) return;
    const first = `${year}-${pad(month + 1)}-01`;
    const last = `${year}-${pad(month + 1)}-${pad(new Date(year, month + 1, 0).getDate())}`;
    apiClient
      .get('/schedule/events', { params: { startDate: first, endDate: last } })
      .then(r => {
        const set = new Set<string>();
        for (const e of (r.data?.data || []) as Array<{ scheduled_date: string; end_date?: string }>) {
          const [sy, sm, sd] = String(e.scheduled_date).slice(0, 10).split('-').map(Number);
          const [ey, em, ed] = String(e.end_date || e.scheduled_date).slice(0, 10).split('-').map(Number);
          let cur = new Date(sy, sm - 1, sd);
          const end = new Date(ey, em - 1, ed);
          while (cur <= end) {
            set.add(dayKey(cur));
            cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
          }
        }
        setScheduledDays(set);
      })
      .catch(() => setScheduledDays(new Set()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, year, month]);

  // Coming up (CR #206) — next 7 days of dated records, kept to a few rows
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  useEffect(() => {
    if (!open) return;
    const start = new Date();
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
    getAgenda(dayKey(start), dayKey(end))
      .then(items => setAgenda(items.filter(i => i.type !== 'schedule')))
      .catch(() => setAgenda([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // BC statutory holidays for the visible year (CR #200)
  const holidayNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of bcStatHolidays(year)) m.set(h.date, h.name);
    return m;
  }, [year]);

  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  const isToday = (d: Date) => d.toDateString() === today.toDateString();

  // ── To-dos ──────────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [newTodo, setNewTodo] = useState('');
  useEffect(() => {
    if (open) setTasks(readTasks());
  }, [open]);

  const toggleTask = (id: string) => {
    const next = readTasks().map(t => (t.id === id ? { ...t, done: !t.done } : t));
    writeTasks(next);
    setTasks(next);
  };
  const addTodo = () => {
    const text = newTodo.trim();
    if (!text) return;
    const next = [
      { id: `t${Date.now()}`, text, done: false, category: 'todo' as const, priority: 'medium' as const, createdAt: new Date().toISOString() },
      ...readTasks(),
    ];
    writeTasks(next);
    setTasks(next);
    setNewTodo('');
  };
  const openTodos = tasks.filter(t => !t.done);

  // ── Quick-glance stats (#191) ───────────────────────────────────────────────
  const [stats, setStats] = useState<DashboardStats | null>(null);
  useEffect(() => {
    if (!open) return;
    getDashboardStats().then(setStats).catch(() => {});
  }, [open]);

  // ── Dev queue (owner only) ──────────────────────────────────────────────────
  const isOwner = user?.role === 'owner';
  const [queue, setQueue] = useState<DevQueueRow[]>([]);
  useEffect(() => {
    if (!open || !isOwner) return;
    apiClient
      .get('/dev/change-requests')
      .then(r => {
        const rows = (r.data?.data ?? []) as DevQueueRow[];
        setQueue(
          rows
            .filter(q => q.status === 'open' || q.status === 'in_progress')
            .sort((a, b) => (b.fulfill_now - a.fulfill_now) || (b.id - a.id))
        );
      })
      .catch(() => setQueue([]));
  }, [open, isOwner]);

  return (
    <div className="relative" ref={ref}>
      {/* Clock trigger */}
      <button
        onClick={() => setOpen(v => !v)}
        className={`relative flex flex-col items-end justify-center h-9 pl-2.5 pr-2.5 rounded-lg leading-tight transition-colors ${
          open ? 'bg-[#C77D3F]/30' : 'hover:bg-white/10'
        }`}
        title="Calendar, notifications & to-dos"
      >
        <span className="text-xs font-semibold text-gray-200 font-mono">
          {now.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false })}
        </span>
        <span className="text-[10px] text-gray-500">
          {now.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
        </span>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1.5 w-[340px] bg-[#1e1e2e]/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl z-[70] overflow-hidden">
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain pb-2">

            {/* Account — merged in from the old separate user menu (#187) */}
            <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-white/10">
              <div className="w-8 h-8 rounded-full bg-[#C77D3F]/25 flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-[#C77D3F] text-base">person</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-100 truncate">{user?.username}</p>
                <p className="text-[11px] text-gray-500 truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => launch('/settings', 'Settings', 'manage_accounts')}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-[#C77D3F] hover:bg-white/10"
                title="Account settings"
              >
                <span className="material-symbols-outlined text-lg">manage_accounts</span>
              </button>
              <button
                onClick={logout}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10"
                title="Sign out"
              >
                <span className="material-symbols-outlined text-lg">logout</span>
              </button>
            </div>

            {/* Quick-glance numbers (#191) — tap any to open the full page */}
            {stats && (
              <div className="grid grid-cols-3 gap-1.5 px-2 pt-2">
                {[
                  { label: 'Active jobs', value: String(stats.activeJobs), route: '/jobs', title: 'Jobs', icon: 'construction' },
                  { label: 'A/R owing', value: `$${Math.round(Number(stats.arOutstanding) / 1000)}k`, route: '/invoices', title: 'Invoices', icon: 'receipt_long' },
                  { label: 'Overdue', value: String(stats.overdueCount), route: '/invoices', title: 'Invoices', icon: 'receipt_long' },
                ].map(t => (
                  <button
                    key={t.label}
                    onClick={() => launch(t.route, t.title, t.icon)}
                    className="rounded-xl bg-black/20 border border-white/5 px-2 py-1.5 text-left hover:bg-white/10"
                  >
                    <span className="block text-[9px] font-bold uppercase tracking-wider text-gray-500">{t.label}</span>
                    <span className={`block text-sm font-bold ${t.label === 'Overdue' && Number(t.value) > 0 ? 'text-red-400' : 'text-gray-100'}`}>{t.value}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Shortcuts — the icons that used to live loose on the bar; the
                Dashboard entry lives here now instead of the main menu (#191) */}
            <div className="grid grid-cols-4 gap-1.5 p-2 border-b border-white/10">
              {[
                { route: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
                { route: '/my-tasks', label: 'My Tasks', icon: 'checklist' },
                ...(isOwner ? [{ route: '/dev', label: 'Dev Dash', icon: 'developer_mode' }] : []),
                { route: '/settings', label: 'Settings', icon: 'settings' },
              ].map(s => (
                <button
                  key={s.route}
                  onClick={() => launch(s.route, s.label, s.icon)}
                  className="flex flex-col items-center gap-1 py-2 rounded-xl text-gray-300 hover:bg-white/10 hover:text-[#C77D3F] transition-colors"
                >
                  <span className="material-symbols-outlined text-xl">{s.icon}</span>
                  <span className="text-[10px] font-medium">{s.label}</span>
                </button>
              ))}
            </div>

            {/* Mini month calendar */}
            <SectionTitle
              icon="calendar_month"
              label="Calendar"
              right={
                <button onClick={() => launch('/calendar', 'Calendar', 'calendar_month')} className="text-[11px] font-medium text-gray-500 hover:text-[#C77D3F]">
                  Open full
                </button>
              }
            />
            <div className="px-3">
              <div className="flex items-center justify-between mb-1">
                <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-1 text-gray-500 hover:text-gray-200 rounded" aria-label="Previous month">
                  <span className="material-symbols-outlined text-base">chevron_left</span>
                </button>
                <span className="text-sm font-semibold text-gray-200">
                  {cursor.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })}
                </span>
                <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-1 text-gray-500 hover:text-gray-200 rounded" aria-label="Next month">
                  <span className="material-symbols-outlined text-base">chevron_right</span>
                </button>
              </div>
              <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i} className="text-center text-[10px] font-semibold text-gray-500 py-0.5">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {cells.map((d, i) =>
                  d ? (
                    <button
                      key={i}
                      onClick={() => launch(calRoute(d), 'Calendar', 'calendar_month')}
                      title={[
                        holidayNames.get(dayKey(d)) ? `STAT · ${holidayNames.get(dayKey(d))}` : '',
                        scheduledDays.has(dayKey(d)) ? 'Scheduled work' : '',
                      ].filter(Boolean).join(' · ') || undefined}
                      className={`relative h-7 text-xs rounded-lg transition-colors ${
                        isToday(d) ? 'bg-[#C77D3F] text-white font-bold' : 'text-gray-300 hover:bg-[#C77D3F]/20 hover:text-[#C77D3F]'
                      }`}
                    >
                      {d.getDate()}
                      {scheduledDays.has(dayKey(d)) && (
                        <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${isToday(d) ? 'bg-white' : 'bg-[#C77D3F]'}`} />
                      )}
                      {holidayNames.has(dayKey(d)) && (
                        <span className={`absolute top-0.5 right-0.5 w-1 h-1 rounded-full ${isToday(d) ? 'bg-white' : 'bg-red-400'}`} />
                      )}
                    </button>
                  ) : (
                    <div key={i} />
                  )
                )}
              </div>
              {/* Coming up — compact 7-day agenda (CR #206) */}
              {agenda.length > 0 && (
                <div className="mt-2 rounded-xl bg-black/20 border border-white/5 py-1">
                  {agenda.slice(0, 4).map((it, i) => (
                    <button
                      key={i}
                      onClick={() => launch(it.link, AGENDA_META[it.type].name, AGENDA_META[it.type].icon)}
                      className="w-full flex items-center gap-2 px-2.5 py-1 text-left hover:bg-white/5"
                      title={`${AGENDA_META[it.type].name} — ${it.label}`}
                    >
                      <span className="material-symbols-outlined text-sm text-[#C77D3F] flex-shrink-0">{AGENDA_META[it.type].icon}</span>
                      <span className="flex-1 min-w-0 truncate text-[11px] text-gray-200">{it.label}</span>
                      <span className="flex-shrink-0 text-[10px] text-gray-500">
                        {new Date(it.date + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                      </span>
                    </button>
                  ))}
                  {agenda.length > 4 && (
                    <button
                      onClick={() => launch('/calendar', 'Calendar', 'calendar_month')}
                      className="w-full px-2.5 py-0.5 text-left text-[10px] text-gray-500 hover:text-[#C77D3F]"
                    >
                      +{agenda.length - 4} more this week
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Notifications */}
            <SectionTitle
              icon="notifications"
              label={unread > 0 ? `Notifications (${unread})` : 'Notifications'}
              right={
                unread > 0 ? (
                  <button
                    onClick={async () => {
                      await markAllRead().catch(() => {});
                      setNotes(prev => prev.map(x => ({ ...x, is_read: true })));
                      setUnread(0);
                    }}
                    className="text-[11px] font-medium text-gray-500 hover:text-[#C77D3F]"
                  >
                    Mark all read
                  </button>
                ) : undefined
              }
            />
            <div className="mx-2 rounded-xl bg-black/20 border border-white/5 max-h-56 overflow-y-auto overscroll-contain">
              {notes.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-gray-500">All caught up!</p>
              ) : (
                notes.slice(0, 12).map(n => (
                  <div
                    key={n.id}
                    onClick={() => clickNote(n)}
                    className={`flex items-start gap-2.5 px-3 py-2 border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/5 ${!n.is_read ? 'bg-[#C77D3F]/10' : ''}`}
                  >
                    <span className={`material-symbols-outlined text-base mt-0.5 flex-shrink-0 ${!n.is_read ? 'text-[#C77D3F]' : 'text-gray-500'}`}>
                      {notifIcon[n.type] ?? notifIcon.default}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-snug ${!n.is_read ? 'font-semibold text-gray-100' : 'text-gray-300'}`}>{n.title}</p>
                      {n.body && <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>}
                      <p className="text-[10px] text-gray-600 mt-0.5">
                        {new Date(n.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <button
                      onClick={async e => {
                        e.stopPropagation();
                        await deleteNotification(n.id).catch(() => {});
                        setNotes(prev => prev.filter(x => x.id !== n.id));
                        if (!n.is_read) setUnread(prev => Math.max(0, prev - 1));
                      }}
                      className="text-gray-600 hover:text-gray-300 flex-shrink-0 mt-0.5"
                      title="Dismiss"
                    >
                      <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* To-dos — same list as /my-tasks */}
            <SectionTitle
              icon="checklist"
              label={openTodos.length > 0 ? `To-dos (${openTodos.length})` : 'To-dos'}
              right={
                <button onClick={() => launch('/my-tasks', 'My Tasks', 'checklist')} className="text-[11px] font-medium text-gray-500 hover:text-[#C77D3F]">
                  Open all
                </button>
              }
            />
            <div className="mx-2 rounded-xl bg-black/20 border border-white/5">
              <div className="flex items-center gap-2 px-2.5 pt-2">
                <input
                  value={newTodo}
                  onChange={e => setNewTodo(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addTodo(); }}
                  placeholder="Add a to-do…"
                  className="flex-1 min-w-0 h-8 px-2 rounded-lg bg-white/5 text-xs text-gray-200 placeholder-gray-500 border border-white/10 focus:outline-none focus:border-[#C77D3F]/50"
                />
                <button
                  onClick={addTodo}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#C77D3F] hover:bg-white/10 flex-shrink-0"
                  title="Add"
                >
                  <span className="material-symbols-outlined text-lg">add</span>
                </button>
              </div>
              <div className="max-h-44 overflow-y-auto overscroll-contain py-1">
                {openTodos.length === 0 ? (
                  <p className="px-3 py-3 text-center text-xs text-gray-500">Nothing outstanding</p>
                ) : (
                  openTodos.slice(0, 8).map(t => (
                    <button
                      key={t.id}
                      onClick={() => toggleTask(t.id)}
                      className="w-full flex items-start gap-2.5 px-3 py-1.5 text-left hover:bg-white/5"
                      title="Mark done"
                    >
                      <span className="material-symbols-outlined text-base text-gray-500 mt-px flex-shrink-0">radio_button_unchecked</span>
                      <span className="flex-1 min-w-0 text-xs text-gray-200 leading-snug">{t.text}</span>
                      {t.priority === 'high' && (
                        <span className="text-[9px] font-bold uppercase text-red-400 mt-0.5 flex-shrink-0">high</span>
                      )}
                    </button>
                  ))
                )}
                {openTodos.length > 8 && (
                  <p className="px-3 py-1 text-[10px] text-gray-500">+{openTodos.length - 8} more in My Tasks</p>
                )}
              </div>
            </div>

            {/* Dev task queue — owner only */}
            {isOwner && (
              <>
                <SectionTitle
                  icon="developer_mode"
                  label={queue.length > 0 ? `Dev queue (${queue.length})` : 'Dev queue'}
                  right={
                    <button onClick={() => launch('/dev', 'Dev Dashboard', 'developer_mode')} className="text-[11px] font-medium text-gray-500 hover:text-[#C77D3F]">
                      Open all
                    </button>
                  }
                />
                <div className="mx-2 rounded-xl bg-black/20 border border-white/5 max-h-40 overflow-y-auto overscroll-contain py-1">
                  {queue.length === 0 ? (
                    <p className="px-3 py-3 text-center text-xs text-gray-500">Queue is clear</p>
                  ) : (
                    queue.slice(0, 6).map(q => (
                      <button
                        key={q.id}
                        onClick={() => launch('/dev', 'Dev Dashboard', 'developer_mode')}
                        className="w-full flex items-start gap-2 px-3 py-1.5 text-left hover:bg-white/5"
                      >
                        <span className="text-[10px] font-mono text-gray-500 mt-0.5 flex-shrink-0">#{q.id}</span>
                        <span className="flex-1 min-w-0 text-xs text-gray-200 leading-snug truncate">{q.title}</span>
                        {q.fulfill_now === 1 && (
                          <span className="text-[9px] font-bold uppercase text-[#C77D3F] mt-0.5 flex-shrink-0">now</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}

            {/* Desktop Mode trial toggle — turning it off restores the classic app */}
            <button
              onClick={() => {
                const next = !desktopModeOn;
                setDesktopModeOn(next);
                setOpen(false);
                navigate(next ? '/desktop' : '/dashboard');
              }}
              className="w-[calc(100%-1rem)] mx-2 mt-2 flex items-center gap-3 min-h-[40px] px-3 rounded-xl text-sm text-gray-200 bg-black/20 border border-white/5 hover:bg-white/10 transition-colors"
              title="Trial feature: wallpaper desktop with widgets, folders and windowed apps"
            >
              <span className={`material-symbols-outlined text-[18px] ${desktopModeOn ? 'text-[#C77D3F]' : 'text-gray-400'}`}>desktop_windows</span>
              <span className="flex-1 text-left">Desktop Mode</span>
              <span className={`flex h-5 w-9 items-center rounded-full px-0.5 transition-colors ${desktopModeOn ? 'bg-[#C77D3F]' : 'bg-white/20'}`}>
                <span className={`h-4 w-4 rounded-full bg-white transition-transform ${desktopModeOn ? 'translate-x-4' : ''}`} />
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
